import { useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Check, Info, Sparkles, Loader2, HelpCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useWizard } from './WizardContext';
import { ProgressIndicator } from './ProgressIndicator';
import { cn } from '@/lib/utils';
import { useCaseSuggestionsApi } from '@/services/api';

// Info tooltip component for options
const InfoTooltip = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!content) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            aria-label="More information"
          >
            <Info className="w-4 h-4 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          align="start"
          className="max-w-xs p-3 text-sm leading-relaxed"
          sideOffset={8}
        >
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const OptionCard = ({ option, isSelected, onClick, type }) => {
  const IconComponent = LucideIcons[option.icon] || LucideIcons.Circle;
  const hasTooltip = !!option.tooltip;
  
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onClick}
        data-testid={`option-${option.id}`}
        className={cn(
          'option-card text-left flex-1',
          isSelected && 'selected'
        )}
        type="button"
      >
        {/* Selection indicator */}
        <div className={cn(
          'option-indicator mt-0.5',
          type === 'multi-select' && 'checkbox'
        )}>
          {isSelected && (
            <Check className="w-3 h-3 text-primary-foreground" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex items-center gap-3 flex-1">
          <div className={cn(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
            isSelected ? 'bg-primary/10' : 'bg-muted'
          )}>
            <IconComponent className={cn(
              'w-5 h-5 transition-colors',
              isSelected ? 'text-primary' : 'text-muted-foreground'
            )} />
          </div>
          <span className="font-medium text-foreground">{option.label}</span>
        </div>
      </button>
      
      {/* Info tooltip - outside the button for better accessibility */}
      {hasTooltip && (
        <InfoTooltip content={option.tooltip} />
      )}
    </div>
  );
};

// Confidence badge styling
const getConfidenceBadgeStyles = (confidence) => {
  switch (confidence) {
    case 'High':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'Medium':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'Low':
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

// "Not Sure" Helper Component - AI-Powered Platform Recommendations
const NotSureHelper = ({ onApplySuggestions, questionOptions }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [aiPlatforms, setAiPlatforms] = useState([]);
  const [aiExplanation, setAiExplanation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('categories'); // 'categories' or 'platforms'

  const handleGetSuggestions = useCallback(async () => {
    if (!description.trim() || description.trim().length < 5) {
      setError('Please describe what you want to do (at least 5 characters)');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);
    setAiPlatforms([]);
    setAiExplanation('');

    try {
      // First, try AI-powered recommendations
      const aiResponse = await useCaseSuggestionsApi.aiRecommend(description.trim());
      
      if (aiResponse.platforms && aiResponse.platforms.length > 0) {
        setAiPlatforms(aiResponse.platforms);
        setAiExplanation(aiResponse.explanation || '');
        setMode('platforms');
        
        // Also get category suggestions for the wizard
        if (aiResponse.categories && aiResponse.categories.length > 0) {
          const validOptionIds = questionOptions.map(opt => opt.id);
          const matchedCategories = aiResponse.categories
            .filter(cat => validOptionIds.includes(cat))
            .map(cat => ({
              optionValue: cat,
              label: questionOptions.find(opt => opt.id === cat)?.label || cat,
              confidence: 'High'
            }));
          setSuggestions(matchedCategories);
        }
      } else {
        // Fall back to keyword-based suggestions
        const response = await useCaseSuggestionsApi.suggest(description.trim());
        if (response.suggestions && response.suggestions.length > 0) {
          setSuggestions(response.suggestions);
          setMode('categories');
        } else {
          setError('No matching results found. Try describing your needs differently.');
        }
      }
    } catch (err) {
      setError('Failed to get suggestions. Please try again.');
      console.error('Suggestion error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [description, questionOptions]);

  const handleApply = () => {
    // Get valid option IDs from the question
    const validOptionIds = questionOptions.map(opt => opt.id);
    // Filter suggestions to only include valid options
    const validSuggestionIds = suggestions
      .map(s => s.optionValue)
      .filter(id => validOptionIds.includes(id));
    
    if (validSuggestionIds.length > 0) {
      onApplySuggestions(validSuggestionIds);
      // Reset state after applying
      setIsExpanded(false);
      setDescription('');
      setSuggestions([]);
      setAiPlatforms([]);
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        data-testid="not-sure-toggle"
        className="mt-4 w-full flex items-center gap-3 p-4 text-left rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all group"
      >
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <span className="font-medium text-foreground block">Not sure? Ask our AI assistant</span>
          <span className="text-sm text-muted-foreground">Describe what you want to do and we'll find the right platforms</span>
        </div>
        <HelpCircle className="w-5 h-5 text-amber-500 opacity-70 group-hover:opacity-100 transition-opacity shrink-0" />
      </button>
    );
  }

  return (
    <div 
      className="mt-6 p-4 bg-muted/50 rounded-lg border border-border"
      data-testid="not-sure-panel"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="font-medium text-sm">AI Assistant - Describe what you need</span>
        </div>
        <button
          onClick={() => {
            setIsExpanded(false);
            setDescription('');
            setSuggestions([]);
            setAiPlatforms([]);
            setAiExplanation('');
            setError(null);
          }}
          className="text-muted-foreground hover:text-foreground text-sm"
          data-testid="not-sure-close"
        >
          Close
        </button>
      </div>

      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="e.g., I want to make an animation and narrate over it, or I need help writing blog posts with images..."
        className="min-h-[80px] mb-3 resize-none"
        data-testid="not-sure-textarea"
      />

      <Button
        onClick={handleGetSuggestions}
        disabled={isLoading || description.trim().length < 5}
        size="lg"
        className="gap-2 w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3"
        data-testid="suggest-categories-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            AI is thinking...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Find platforms for me
          </>
        )}
      </Button>

      {error && (
        <p className="mt-3 text-sm text-destructive" data-testid="not-sure-error">
          {error}
        </p>
      )}

      {/* AI Platform Recommendations */}
      {aiPlatforms.length > 0 && (
        <div className="mt-4" data-testid="ai-platforms-panel">
          {aiExplanation && (
            <p className="text-sm text-muted-foreground mb-3 p-2 bg-primary/5 rounded-md">
              <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
              {aiExplanation}
            </p>
          )}
          <p className="text-sm font-medium text-foreground mb-2">
            Recommended Platforms:
          </p>
          <div className="space-y-2 mb-4">
            {aiPlatforms.map((platform) => (
              <a
                key={platform.id}
                href={`/platform/${platform.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 bg-background rounded-md border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-colors"
                data-testid={`ai-platform-${platform.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-foreground">{platform.name}</span>
                    {platform.provider && (
                      <span className="text-xs text-muted-foreground">by {platform.provider}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {platform.summary}
                  </p>
                  {platform.tags && platform.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {platform.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Category Suggestions for wizard flow */}
      {suggestions.length > 0 && (
        <div className={cn("mt-4", aiPlatforms.length > 0 && "pt-4 border-t border-border")} data-testid="suggestions-panel">
          <p className="text-sm text-muted-foreground mb-2">
            {aiPlatforms.length > 0 ? "Or continue with the comparison wizard:" : "Suggested categories:"}
          </p>
          <div className="space-y-2 mb-4">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.optionValue}
                className="flex items-center justify-between p-2 bg-background rounded-md border border-border/50"
                data-testid={`suggestion-${suggestion.optionValue}`}
              >
                <span className="font-medium text-sm">{suggestion.label}</span>
                <span className={cn(
                  'text-xs px-2 py-0.5 rounded-full font-medium',
                  getConfidenceBadgeStyles(suggestion.confidence)
                )}>
                  {suggestion.confidence}
                </span>
              </div>
            ))}
          </div>
          <Button
            onClick={handleApply}
            size="sm"
            className="gap-2"
            data-testid="apply-suggestions-btn"
          >
            <Check className="w-4 h-4" />
            Use these categories
          </Button>
        </div>
      )}
    </div>
  );
};

export const QuestionScreen = () => {
  const { 
    currentStep, 
    totalQuestions,
    answers, 
    direction,
    goToNext, 
    goToPrevious, 
    setAnswer, 
    toggleMultiAnswer,
    getCurrentQuestion,
    canProceed,
    calculateResults,
  } = useWizard();

  const question = getCurrentQuestion();
  
  if (!question) return null;

  const currentAnswer = answers[question.id];
  const isLastQuestion = currentStep === totalQuestions;
  const hasTooltips = question.options.some(opt => opt.tooltip);
  
  // Check if this is the "purpose" question (step 1) which allows the "Not sure" helper
  const isPurposeQuestion = question.id === 'purpose';

  const handleOptionClick = (optionId) => {
    if (question.type === 'multi-select') {
      toggleMultiAnswer(question.id, optionId);
    } else {
      setAnswer(question.id, optionId);
    }
  };

  const isOptionSelected = (optionId) => {
    if (question.type === 'multi-select') {
      return currentAnswer && currentAnswer.includes(optionId);
    }
    return currentAnswer === optionId;
  };

  const handleNext = () => {
    if (isLastQuestion) {
      calculateResults();
    }
    goToNext();
  };

  // Handle applying suggestions from the NotSureHelper
  const handleApplySuggestions = (suggestionIds) => {
    // For multi-select, we add all suggested options
    if (question.type === 'multi-select') {
      suggestionIds.forEach(id => {
        // Only toggle if not already selected
        if (!currentAnswer || !currentAnswer.includes(id)) {
          toggleMultiAnswer(question.id, id);
        }
      });
    }
  };

  // Filter out "not-sure" option from main list if it exists (we have the NotSureHelper instead)
  const displayOptions = isPurposeQuestion 
    ? question.options.filter(opt => opt.id !== 'not-sure')
    : question.options;

  return (
    <div className={cn(
      direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left'
    )}>
      {/* Progress */}
      <div className="mb-8">
        <ProgressIndicator />
      </div>

      {/* Question */}
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-2">
          {question.title}
        </h2>
        <p className="text-muted-foreground">
          {question.subtitle}
        </p>
        {question.type === 'multi-select' && (
          <p className="text-sm text-muted-foreground mt-1">
            Select all that apply
          </p>
        )}
      </div>

      {/* Options */}
      <div className="space-y-3 mb-4">
        {displayOptions.map((option) => (
          <OptionCard
            key={option.id}
            option={option}
            isSelected={isOptionSelected(option.id)}
            onClick={() => handleOptionClick(option.id)}
            type={question.type}
          />
        ))}
      </div>

      {/* Helper text for tooltips */}
      {hasTooltips && question.helperText && (
        <p className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          {question.helperText}
        </p>
      )}

      {/* "Not Sure" Helper - only for purpose question */}
      {isPurposeQuestion && (
        <NotSureHelper 
          onApplySuggestions={handleApplySuggestions}
          questionOptions={displayOptions}
        />
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-border mt-6">
        <Button
          variant="ghost"
          onClick={goToPrevious}
          className="gap-2"
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <Button
          onClick={handleNext}
          disabled={!canProceed()}
          className="gap-2"
          data-testid="wizard-continue-btn"
        >
          {isLastQuestion ? 'See results' : 'Continue'}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
